/**
 * Words in a poem → syllables and lexical stress, from the CMU Pronouncing Dictionary.
 *
 * Every poetry-analysis tool reads a line the same way, so this is the one place that
 * decides how many syllables a word has and which of them carry stress:
 *
 * - **Dictionary words** — the CMU entry (ARPAbet with stress digits 0/1/2). The bundled
 *   copy keeps only the first pronunciation of each word, so a word poets say two ways
 *   ("fire", "every", "heaven") carries a list of *alternatives* built from documented
 *   verse conventions (see `alternativesFor`). The default is always the dictionary's.
 * - **Poetic elisions** — "o'er", "heav'n", "'tis", "lov'd" are mapped to their spoken form.
 * - **Words the dictionary lacks** — names, coinages, rare archaisms. Their syllable count
 *   is *estimated* from spelling (`estimateSyllables`), their stress is unknown, and the
 *   word is flagged so a tool can say so.
 *
 * Nothing here touches the network or the DOM; the dictionary arrives as a `Lookup`.
 */

export type Stress = 0 | 1 | 2;
/** `null` — the stress of a syllable in a word whose pronunciation is only estimated. */
export type StressMark = Stress | null;

/** Why a reading differs from the dictionary's; the widget turns each code into words. */
export type Reason =
  | "diphthong-er"
  | "syncope"
  | "full-form"
  | "ed-ending"
  | "ion-ending"
  | "elision"
  | "estimate";

export interface Reading {
  syllables: number;
  /** One entry per syllable. */
  stress: StressMark[];
  /** Null for the default reading. */
  reason: Reason | null;
}

export type Source = "dictionary" | "poetic" | "elided" | "estimated";

export interface WordInfo {
  /** As written, without surrounding quotation marks. */
  text: string;
  /** Lower case, straight apostrophes. */
  key: string;
  /** Offsets of `text` in the line it came from. */
  start: number;
  end: number;
  source: Source;
  /** ARPAbet phones with stress digits; null for an estimated word. */
  phones: string[] | null;
  /** The reading used unless the reader picks another. */
  reading: Reading;
  /** Other readings with a different syllable count. */
  alternatives: Reading[];
  /** A one-syllable word that carries no fixed stress in speech (the, of, and, to…). */
  functionWord: boolean;
}

export interface LineInfo {
  /** The line as typed (not trimmed). */
  text: string;
  /** 1-based line number in the input, counting blank lines. */
  number: number;
  words: WordInfo[];
  /** True when the line has digits, which are not counted. */
  hasDigits: boolean;
}

export type Lookup = (key: string) => string | null;

/* ------------------------------------------------------------------ tokens */

const RE_TOKEN = /['’]?\p{L}+(?:['’]\p{L}+)*['’]?/gu;
const RE_APOSTROPHES = /’/g;
const RE_DIGIT = /\d/;
const RE_NEWLINE = /\r?\n/;
const RE_STRESS_DIGIT = /\d$/;
const RE_SPACE = /\s+/;
const RE_TRAILING_APOSTROPHE = /'$/;

/** Elided forms with a leading or trailing apostrophe the dictionary cannot hold. */
const POETIC: Readonly<Record<string, string>> = {
  "o'er": "AO1 R",
  "e'er": "EH1 R",
  "e'en": "IY1 N",
  "'tis": "T IH1 Z",
  "'twas": "T W AH1 Z",
  "'twere": "T W ER1",
  "'twill": "T W IH1 L",
  "'twould": "T W UH1 D",
  "'twixt": "T W IH1 K S T",
  "'gainst": "G EH1 N S T",
  "'mid": "M IH1 D",
  "'midst": "M IH1 D S T",
  "'neath": "N IY1 TH",
  "'bove": "B AH1 V",
  "'round": "R AW1 N D",
  "'fore": "F AO1 R",
  "heav'n": "HH EH1 V N",
  "ev'n": "IY1 V N",
  "ev'ry": "EH1 V R IY0",
  "pow'r": "P AW1 R",
  "flow'r": "F L AW1 R",
  "flow'rs": "F L AW1 R Z",
  "show'r": "SH AW1 R",
  "tow'r": "T AW1 R",
  "pow'rs": "P AW1 R Z",
  "wat'ry": "W AO1 T R IY0",
  "gen'ral": "JH EH1 N R AH0 L",
  "mem'ry": "M EH1 M R IY0",
  "mis'ry": "M IH1 Z R IY0",
  "bett'ring": "B EH1 T R IH0 NG",
  "wand'ring": "W AA1 N D R IH0 NG",
  "murm'ring": "M ER1 M R IH0 NG",
  "gard'ner": "G AA1 R D N ER0",
  "pris'ner": "P R IH1 Z N ER0",
  "whisp'ring": "W IH1 S P R IH0 NG",
  "ling'ring": "L IH1 NG G R IH0 NG",
  "flick'ring": "F L IH1 K R IH0 NG",
  "gath'ring": "G AE1 DH R IH0 NG",
};

/** Elided clitics written "th'" and "t'": they add no syllable of their own. */
const CLITICS: ReadonlySet<string> = new Set(["th", "t"]);

/** Suffix apostrophe forms handled as ordinary English, not as elisions. */
const RE_SUFFIX_CONTRACTION = /^[a-z]+'(?:s|d|ll|re|ve|m|t|st)$/;

/* ----------------------------------------------------------- function words */

/**
 * One-syllable words that speakers freely stress or leave unstressed. The dictionary
 * writes "to" as T UW1 and "the" as DH AH0, but in a line of verse either can fall on a
 * beat or off it, so scansion treats them as flexible rather than trusting the digit.
 */
const FUNCTION_WORDS: ReadonlySet<string> = new Set(
  (
    "a an the and but or nor for so yet if as at by in of on to up off out from with than " +
    "that this these those who whom whose which what when where while he she it we they " +
    "you i me him her us them my your his its our their mine thine thy thee thou ye am is " +
    "are was were be been do does did have has had can could shall should will would may " +
    "might must not no there here then how why all any each some such too very just also " +
    "art wilt shalt hast hath dost doth didst canst wert wast more most much many less few well per ere nor 'tis 'twas o'er e'er ne'er e'en 'gainst 'mid 'neath 'bove it's i'm i'll " +
    "i've i'd he's she's we're they're you're that's there's don't can't won't isn't " +
    "aren't wasn't didn't couldn't shouldn't wouldn't hasn't haven't doesn't"
  ).split(RE_SPACE)
);

export function isFunctionWord(key: string): boolean {
  return FUNCTION_WORDS.has(key);
}

/* --------------------------------------------------------------- ARPAbet */

function splitPhones(pron: string): string[] {
  return pron.trim().split(RE_SPACE);
}

function isVowelPhone(phone: string): boolean {
  return RE_STRESS_DIGIT.test(phone);
}

function stressOf(phones: readonly string[]): Stress[] {
  return phones
    .filter(isVowelPhone)
    .map((phone) => Number(phone.slice(-1)) as Stress);
}

function readingOf(phones: readonly string[], reason: Reason | null): Reading {
  const stress = stressOf(phones);
  return { syllables: stress.length, stress, reason };
}

const SIBILANTS: ReadonlySet<string> = new Set([
  "S",
  "Z",
  "SH",
  "ZH",
  "CH",
  "JH",
]);
const VOICELESS: ReadonlySet<string> = new Set(["P", "T", "K", "F", "TH"]);

/** Phones of "<base>'s" or a plural from the base's phones. */
function withSuffixS(base: readonly string[]): string[] {
  const last = base.at(-1) ?? "";
  if (SIBILANTS.has(last)) {
    return [...base, "IH0", "Z"];
  }
  return [...base, VOICELESS.has(last) ? "S" : "Z"];
}

/* ------------------------------------------------------------ alternatives */

/** Words where verse often drops one unstressed syllable ("fam'ly", "heav'n"). */
const SYNCOPE: Readonly<Record<string, number>> = {
  every: 2,
  memory: 1,
  history: 2,
  family: 1,
  different: 1,
  general: 1,
  favorite: 1,
  average: 1,
  factory: 1,
  heaven: 1,
  heavens: 1,
  seven: 1,
  eleven: 2,
  given: 1,
  driven: 1,
  woven: 1,
  proven: 1,
  over: 1,
  ever: 1,
  never: 1,
  cruel: 1,
  jewel: 1,
  ruin: 1,
  evening: 1,
  prisoner: 1,
  gardener: 1,
  misery: 1,
  memories: 1,
  flowery: 1,
  slippery: 1,
};

/**
 * Words the dictionary reduces ("temperate" as tem-prit) that verse often sounds in full.
 * Each is the full ARPAbet, with the middle vowel restored.
 */
const FULL_FORMS: Readonly<Record<string, string>> = {
  temperate: "T EH1 M P ER0 AH0 T",
  desperate: "D EH1 S P ER0 AH0 T",
  several: "S EH1 V ER0 AH0 L",
  evening: "IY1 V AH0 N IH0 NG",
  interest: "IH1 N T ER0 AH0 S T",
  chocolate: "CH AO1 K AH0 L AH0 T",
  business: "B IH1 Z IH0 N AH0 S",
  restaurant: "R EH1 S T ER0 AA0 N T",
  jewelry: "JH UW1 AH0 L R IY0",
};

const DIPHTHONG_VOWELS: ReadonlySet<string> = new Set([
  "AY",
  "AW",
  "EY",
  "OW",
  "OY",
  "IY",
  "UW",
]);

function dropSyllable(
  phones: readonly string[],
  index: number
): string[] | null {
  let seen = -1;
  const out: string[] = [];
  let dropped = false;
  for (const phone of phones) {
    if (isVowelPhone(phone)) {
      seen += 1;
      if (seen === index) {
        dropped = true;
        continue;
      }
    }
    out.push(phone);
  }
  return dropped ? out : null;
}

function vowelIndexes(phones: readonly string[]): number[] {
  return phones.flatMap((phone, i) => (isVowelPhone(phone) ? [i] : []));
}

/** The syllable to drop for the "fire" / "wandering" / "memory" family, if any. */
function erDropIndex(phones: readonly string[]): number | null {
  const vowels = vowelIndexes(phones);
  for (let v = 1; v < vowels.length; v += 1) {
    const here = phones[vowels[v]];
    const before = phones[vowels[v - 1]];
    if (here !== "ER0") {
      continue;
    }
    if (DIPHTHONG_VOWELS.has(before.slice(0, -1)) && before.slice(-1) !== "0") {
      return v;
    }
  }
  return null;
}

function erEndingDrop(phones: readonly string[]): number | null {
  const stress = vowelIndexes(phones).map((i) => phones[i]);
  const n = stress.length;
  if (n < 3) {
    return null;
  }
  const tail = stress.slice(-2).join(" ");
  if (tail === "ER0 IH0" && phones.at(-1) === "NG") {
    return n - 2;
  }
  if (tail === "ER0 IY0" && phones.at(-1) === "IY0") {
    return n - 2;
  }
  return null;
}

function syllableCountOf(phones: readonly string[]): number {
  return phones.filter(isVowelPhone).length;
}

function edEndingReading(
  key: string,
  phones: readonly string[]
): Reading | null {
  const last = phones.at(-1);
  const before = phones.at(-2);
  if (
    key.length > 3 &&
    key.endsWith("ed") &&
    (last === "D" || last === "T") &&
    before !== undefined &&
    !isVowelPhone(before)
  ) {
    const stress = stressOf(phones);
    return {
      syllables: stress.length + 1,
      stress: [...stress, 0],
      reason: "ed-ending",
    };
  }
  return null;
}

function ionEndingReading(
  key: string,
  phones: readonly string[]
): Reading | null {
  const n = phones.length;
  if (
    key.endsWith("ion") &&
    phones[n - 1] === "N" &&
    phones[n - 2] === "AH0" &&
    ["SH", "ZH", "CH", "JH"].includes(phones[n - 3] ?? "")
  ) {
    const stress = stressOf(phones);
    return {
      syllables: stress.length + 1,
      stress: [...stress.slice(0, -1), 0, 0],
      reason: "ion-ending",
    };
  }
  return null;
}

/** The other ways verse says this word, each with a different syllable count. */
function alternativesFor(key: string, phones: readonly string[]): Reading[] {
  const found: Reading[] = [];
  const base = key.endsWith("'s") ? key.slice(0, -2) : key;
  const diphthong = erDropIndex(phones);
  const ending = erEndingDrop(phones);
  const curated = SYNCOPE[base];
  const drops: [number | null, Reason][] = [
    [diphthong, "diphthong-er"],
    [ending ?? curated ?? null, "syncope"],
  ];
  for (const [index, reason] of drops) {
    const shorter = index === null ? null : dropSyllable(phones, index);
    if (shorter && syllableCountOf(shorter) > 0) {
      found.push(readingOf(shorter, reason));
    }
  }
  for (const reading of [
    edEndingReading(key, phones),
    ionEndingReading(key, phones),
  ]) {
    if (reading) {
      found.push(reading);
    }
  }
  const full = FULL_FORMS[key];
  if (full) {
    found.push(readingOf(splitPhones(full), "full-form"));
  }
  const seen = new Set<number>([syllableCountOf(phones)]);
  return found.filter((reading) => {
    if (seen.has(reading.syllables)) {
      return false;
    }
    seen.add(reading.syllables);
    return true;
  });
}

/* --------------------------------------------------------------- estimate */

const RE_ESTIMATE_LETTERS = /[^a-z]/g;
const RE_SILENT_ENDING = /(?:[^laeiouy]es|[^laeiouytd]ed|[^laeiouy]e)$/;
const RE_LEADING_Y = /^y/;
const RE_VOWEL_GROUP = /[aeiouy]{1,2}/g;
const RE_SOFT_ENDINGS = /(?:[tsc]i(?=on|ous|al|an|en|ent)|gi(?=on|ous)|qu)/g;
const RE_HIATUS = /ia|io(?!n|u)|iu|eo|ua|uo|ao/g;
const RE_ISM = /[^aeiouy]ism$/;

/**
 * A syllable count from spelling alone, for a word the dictionary lacks. A rule of
 * thumb — vowel groups, a silent final e, "-ed" and "-es" endings, a few hiatus pairs —
 * measured against the dictionary in this tool's tests, not a promise.
 */
export function estimateSyllables(word: string): number {
  const letters = word.toLowerCase().replace(RE_ESTIMATE_LETTERS, "");
  if (letters.length === 0) {
    return 0;
  }
  if (letters.length <= 3) {
    return 1;
  }
  let w = letters.replace(RE_SOFT_ENDINGS, "x");
  w = w.replace(RE_SILENT_ENDING, "").replace(RE_LEADING_Y, "");
  let count = w.match(RE_VOWEL_GROUP)?.length ?? 0;
  count += w.match(RE_HIATUS)?.length ?? 0;
  if (RE_ISM.test(letters)) {
    count += 1;
  }
  return Math.max(count, 1);
}

function estimatedReading(word: string): Reading {
  const n = estimateSyllables(word);
  return {
    syllables: n,
    stress: Array.from({ length: n }, () => null),
    reason: null,
  };
}

function estimateAlternatives(reading: Reading): Reading[] {
  const out: Reading[] = [];
  for (const n of [reading.syllables + 1, reading.syllables - 1]) {
    if (n >= 1) {
      out.push({
        syllables: n,
        stress: Array.from({ length: n }, () => null),
        reason: "estimate",
      });
    }
  }
  return out;
}

/* ----------------------------------------------------------------- lookup */

interface Found {
  phones: string[];
  source: Source;
}

function removeNearest(
  phones: readonly string[],
  ratio: number
): string[] | null {
  const stress = stressOf(phones);
  const target = Math.min(
    stress.length - 1,
    Math.max(0, Math.round(ratio * stress.length))
  );
  const candidates = stress
    .map((s, i) => ({ s, i }))
    .filter((entry) => entry.s === 0 && entry.i > 0);
  if (candidates.length === 0) {
    return null;
  }
  candidates.sort(
    (a, b) => Math.abs(a.i - target) - Math.abs(b.i - target) || b.i - a.i
  );
  return dropSyllable(phones, candidates[0].i);
}

const VOWEL_LETTERS = ["e", "a", "i", "o", "u"];

/** "heav'n" → heaven minus one unstressed syllable. */
function elidedForm(key: string, lookup: Lookup): string[] | null {
  const at = key.indexOf("'");
  if (at <= 0 || at >= key.length - 1 || RE_SUFFIX_CONTRACTION.test(key)) {
    return null;
  }
  for (const vowel of VOWEL_LETTERS) {
    const joined = key.slice(0, at) + vowel + key.slice(at + 1);
    const pron = lookup(joined);
    if (pron) {
      return removeNearest(splitPhones(pron), at / key.length);
    }
  }
  return null;
}

function findPhones(key: string, lookup: Lookup): Found | null {
  const poetic = POETIC[key];
  if (poetic) {
    return { phones: splitPhones(poetic), source: "poetic" };
  }
  const direct = lookup(key);
  if (direct) {
    return { phones: splitPhones(direct), source: "dictionary" };
  }
  if (key.endsWith("'st")) {
    // lov'st, wander'st, ow'st: the archaic -st ending adds no syllable.
    const stem = key.slice(0, -3);
    const base = lookup(`${stem}e`) ?? lookup(stem);
    if (base) {
      return { phones: [...splitPhones(base), "S", "T"], source: "poetic" };
    }
  }
  if (key.endsWith("'d")) {
    const full = lookup(`${key.slice(0, -2)}ed`);
    if (full) {
      return { phones: splitPhones(full), source: "poetic" };
    }
  }
  if (key.endsWith("'s") || (key.endsWith("s") && !key.includes("'"))) {
    const stem = key.endsWith("'s") ? key.slice(0, -2) : key.slice(0, -1);
    const base = stem ? lookup(stem) : null;
    if (base) {
      return { phones: withSuffixS(splitPhones(base)), source: "dictionary" };
    }
  }
  const elided = elidedForm(key, lookup);
  if (elided) {
    return { phones: elided, source: "elided" };
  }
  return null;
}

/** Everything known about one word. */
export function describeWord(
  text: string,
  start: number,
  lookup: Lookup,
  lineStart = false
): WordInfo {
  const key = text.toLowerCase().replace(RE_APOSTROPHES, "'");
  const base = { text, key, start, end: start + text.length };
  // "May" and "Will" in mid-line are names, not auxiliaries.
  const capitalised = text[0] !== text[0].toLowerCase();
  const functionWord =
    isFunctionWord(key) &&
    (!capitalised || lineStart || key === "i" || key.startsWith("i'"));
  if (CLITICS.has(key)) {
    const reading: Reading = { syllables: 0, stress: [], reason: null };
    return {
      ...base,
      source: "poetic",
      phones: null,
      reading,
      alternatives: [],
      functionWord: true,
    };
  }
  const found = findPhones(key, lookup);
  if (!found) {
    const reading = estimatedReading(key);
    return {
      ...base,
      source: "estimated",
      phones: null,
      reading,
      alternatives: estimateAlternatives(reading),
      functionWord: false,
    };
  }
  const reading = readingOf(found.phones, null);
  return {
    ...base,
    source: found.source,
    phones: found.phones,
    reading,
    alternatives: alternativesFor(key, found.phones),
    functionWord: functionWord && reading.syllables === 1,
  };
}

/** Clean one raw token: drop quote-mark apostrophes, keep elision ones. */
function cleanToken(raw: string, offset: number): [string, number] | null {
  let text = raw;
  let start = offset;
  const lowered = text.toLowerCase().replace(RE_APOSTROPHES, "'");
  if (text.startsWith("'") || text.startsWith("’")) {
    const known = POETIC[lowered.replace(RE_TRAILING_APOSTROPHE, "")];
    if (!known) {
      text = text.slice(1);
      start += 1;
    }
  }
  if (text.endsWith("'") || text.endsWith("’")) {
    const stem = text.slice(0, -1).toLowerCase();
    text = CLITICS.has(stem) ? stem : text.slice(0, -1);
  }
  return text.length > 0 ? [text, start] : null;
}

/** The words of one line, in order. */
export function lineWords(text: string, lookup: Lookup): WordInfo[] {
  const words: WordInfo[] = [];
  for (const match of text.matchAll(RE_TOKEN)) {
    const cleaned = cleanToken(match[0], match.index ?? 0);
    if (cleaned) {
      words.push(
        describeWord(cleaned[0], cleaned[1], lookup, words.length === 0)
      );
    }
  }
  return words;
}

/** Split a poem into lines and read every line. Blank lines are kept for numbering. */
export function readPoem(text: string, lookup: Lookup): LineInfo[] {
  return text.split(RE_NEWLINE).map((line, index) => ({
    text: line,
    number: index + 1,
    words: lineWords(line, lookup),
    hasDigits: RE_DIGIT.test(line),
  }));
}

/** Lines that contain at least one word. */
export function poemLines(text: string, lookup: Lookup): LineInfo[] {
  return readPoem(text, lookup).filter((line) => line.words.length > 0);
}

/* ---------------------------------------------------------------- readings */

/** `overrides` maps a word key to the syllable count the reader chose for it. */
export type Overrides = Readonly<Record<string, number>>;

/** The reading of a word after the reader's choice, if it names a known reading. */
export function chosenReading(word: WordInfo, overrides: Overrides): Reading {
  const wanted = overrides[word.key];
  if (wanted === undefined || wanted === word.reading.syllables) {
    return word.reading;
  }
  return (
    word.alternatives.find((reading) => reading.syllables === wanted) ??
    word.reading
  );
}

/** Syllables in a line under the reader's choices. */
export function lineSyllables(
  words: readonly WordInfo[],
  overrides: Overrides
) {
  return words.reduce(
    (sum, word) => sum + chosenReading(word, overrides).syllables,
    0
  );
}

/** The set of totals a line can reach if every ambiguous word is read either way. */
export function reachableTotals(words: readonly WordInfo[]): Set<number> {
  let totals = new Set<number>([0]);
  for (const word of words) {
    const options = [word.reading, ...word.alternatives].map(
      (reading) => reading.syllables
    );
    const next = new Set<number>();
    for (const total of totals) {
      for (const n of options) {
        next.add(total + n);
      }
    }
    totals = next;
  }
  return totals;
}

/* ------------------------------------------------------------- rhyme part */

/** Index of the phone a rhyme starts at: the last primary-stressed vowel, else the last secondary one, else the first vowel. */
export function rhymeStart(phones: readonly string[]): number {
  for (const digit of ["1", "2"]) {
    for (let i = phones.length - 1; i >= 0; i -= 1) {
      if (phones[i].endsWith(digit)) {
        return i;
      }
    }
  }
  return Math.max(0, vowelIndexes(phones)[0] ?? 0);
}

/**
 * The sounds a rhyme depends on: from the last stressed vowel (primary preferred, then
 * secondary) to the end of the word, with stress digits removed. Null for an estimated
 * word.
 */
export function rhymingPart(phones: readonly string[] | null): string[] | null {
  if (!phones) {
    return null;
  }
  return phones
    .slice(rhymeStart(phones))
    .map((phone) => phone.replace(RE_STRESS_DIGIT, ""));
}
